import { FACEBOOK_AUTOMATION_STAGE_STATUSES } from './facebookAutomationStageStatuses'
import * as selectAudienceUtils from './fbSelectAudienceModalUtils'
import {
  closeSettingModal,
  getSettingModalSelectedPrivacyLevel,
  openClickableSettingModal,
  setSettingModalPrivacyLevel,
} from './fbSettingsUtils'
import { howPeopleFindYouUrl } from './fbUrls'
import { FacebookSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { FBRecommendationKey } from '../../utils/enums'
import * as enums from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import {
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCantUndoFnFromKey,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { shouldUpdateSetting } from '../utils'

// Setting property functions
const TARGET_VALUES = {
  [FacebookSettingKey.PROFILE_EMAIL_DISCOVERABILITY]:
    selectAudienceUtils.FRIENDS_OF_FRIENDS,
  [FacebookSettingKey.PROFILE_PHONE_DISCOVERABILITY]:
    selectAudienceUtils.FRIENDS_OF_FRIENDS,
}

const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => value.name || null)
const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) =>
    selectAudienceUtils.isPrivacySettingAsStrictOrStricter(
      value,
      TARGET_VALUES[key],
    ),
  )

const getCantUndoFn = (key) =>
  getCantUndoFnFromKey(key, (value) =>
    value ? !selectAudienceUtils.isPrivacySettingUndoable(value) : false,
  )
const getIsDisabledFn = () => defaultIsDisabledFn
const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

const PROFILE_EMAIL_DISCOVERABILITY_LABEL = 'People with your email address'
const PROFILE_PHONE_DISCOVERABILITY_LABEL = 'People with your phone number'

// Gather stage

enum StageKey {
  GATHER_SETTING_DISCOVERABILITY = 'GATHER_SETTING_DISCOVERABILITY',
  UPDATE_SETTING_DISCOVERABILITY = 'UPDATE_SETTING_DISCOVERABILITY',
  UNDO_SETTING_DISCOVERABILITY = 'UNDO_SETTING_DISCOVERABILITY',
}

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_DISCOVERABILITY,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: howPeopleFindYouUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredInitialValues } = userContext

      await openClickableSettingModal([PROFILE_EMAIL_DISCOVERABILITY_LABEL])
      const emailDiscoverabilityValue =
        await getSettingModalSelectedPrivacyLevel(
          PROFILE_EMAIL_DISCOVERABILITY_LABEL,
        )
      await closeSettingModal(PROFILE_EMAIL_DISCOVERABILITY_LABEL, 'CANCEL', {
        removeModalManually: true,
      })

      await openClickableSettingModal([PROFILE_PHONE_DISCOVERABILITY_LABEL])
      const phoneDiscoverabilityValue =
        await getSettingModalSelectedPrivacyLevel(
          PROFILE_PHONE_DISCOVERABILITY_LABEL,
        )
      await closeSettingModal(PROFILE_PHONE_DISCOVERABILITY_LABEL, 'CANCEL', {
        removeModalManually: true,
      })

      updateStoredInitialValues(
        {
          [FacebookSettingKey.PROFILE_EMAIL_DISCOVERABILITY]:
            emailDiscoverabilityValue,
          [FacebookSettingKey.PROFILE_PHONE_DISCOVERABILITY]:
            phoneDiscoverabilityValue,
        },
        FBRecommendationKey.FB_DISCOVERABILITY,
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_DISCOVERABILITY,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: howPeopleFindYouUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (!settingsSelected) {
        return null
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.PROFILE_EMAIL_DISCOVERABILITY,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([PROFILE_EMAIL_DISCOVERABILITY_LABEL])
        await setSettingModalPrivacyLevel(
          PROFILE_EMAIL_DISCOVERABILITY_LABEL,
          isDry,
          selectAudienceUtils.FRIENDS_OF_FRIENDS,
        )
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.PROFILE_PHONE_DISCOVERABILITY,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([PROFILE_PHONE_DISCOVERABILITY_LABEL])
        await setSettingModalPrivacyLevel(
          PROFILE_PHONE_DISCOVERABILITY_LABEL,
          isDry,
          selectAudienceUtils.FRIENDS_OF_FRIENDS,
        )
      }

      return true
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_DISCOVERABILITY,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: howPeopleFindYouUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (!settingsSelected) {
        return null
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.PROFILE_EMAIL_DISCOVERABILITY,
          getIsFulfilledFn,
        )
      ) {
        const initialEmailDiscoverability =
          initialValues[FacebookSettingKey.PROFILE_EMAIL_DISCOVERABILITY]

        await openClickableSettingModal([PROFILE_EMAIL_DISCOVERABILITY_LABEL])
        await setSettingModalPrivacyLevel(
          PROFILE_EMAIL_DISCOVERABILITY_LABEL,
          isDry,
          initialEmailDiscoverability,
        )
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.PROFILE_PHONE_DISCOVERABILITY,
          getIsFulfilledFn,
        )
      ) {
        const initialPhoneDiscoverability =
          initialValues[FacebookSettingKey.PROFILE_PHONE_DISCOVERABILITY]

        await openClickableSettingModal([PROFILE_PHONE_DISCOVERABILITY_LABEL])
        await setSettingModalPrivacyLevel(
          PROFILE_PHONE_DISCOVERABILITY_LABEL,
          isDry,
          initialPhoneDiscoverability,
        )
      }

      return true
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
